import React from "react"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import { IFournisseurDocumentModel } from "@common-models/*"
import {
     CONST_FOURNISSEUR_DOCUMENT_ASSURANCE_RC_PRO,
     CONST_FOURNISSEUR_DOCUMENT_EXTRAIT_KBIS,
     CONST_FOURNISSEUR_DOCUMENT_REGULARITE_FISCALE,
     CONST_FOURNISSEUR_DOCUMENT_RIB,
     CONST_FOURNISSEUR_DOCUMENT_VIGILANCE_URSSAF,
} from "@common-constants/*"
import MyGoogleDriveFileViewerInModal from "../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { addFournisseurDocument, getFournisseurDocuments } from "../../fournisseur/core/_requests"
import { deleteFournisseurDocument, replaceFournisseurDocument } from "../../fournisseur/document/core/_requests"

// Documents du fournisseur, présentés avec le même design que les documents du collaborateur (slots requis).
type Cfg = { type: string; label: string; icon: string }
const FOURNISSEUR_DOC_CONFIG: Cfg[] = [
     { type: CONST_FOURNISSEUR_DOCUMENT_EXTRAIT_KBIS, label: "Extrait KBIS", icon: "fa-building" },
     { type: CONST_FOURNISSEUR_DOCUMENT_ASSURANCE_RC_PRO, label: "Assurance responsabilité civile pro.", icon: "fa-shield-halved" },
     { type: CONST_FOURNISSEUR_DOCUMENT_VIGILANCE_URSSAF, label: "Vigilance URSSAF", icon: "fa-file-shield" },
     { type: CONST_FOURNISSEUR_DOCUMENT_REGULARITE_FISCALE, label: "Régularité fiscale", icon: "fa-file-invoice-dollar" },
     { type: CONST_FOURNISSEUR_DOCUMENT_RIB, label: "Relevé d'Identité Bancaire (RIB)", icon: "fa-building-columns" },
]

const ACCEPT = "application/pdf, image/*"

const FournisseurDocumentsSlots = ({ fournisseurId }: { fournisseurId: number }) => {
     const [documents, setDocuments] = React.useState<IFournisseurDocumentModel[]>([])
     const [loading, setLoading] = React.useState<boolean>(true)
     const [busyType, setBusyType] = React.useState<string | null>(null)
     const [dragOverType, setDragOverType] = React.useState<string | null>(null)
     const [activeDoc, setActiveDoc] = React.useState<IFournisseurDocumentModel | null>(null)
     const replaceInputRefs = React.useRef<Record<number, HTMLInputElement | null>>({})

     React.useEffect(() => {
          if (!fournisseurId) return
          setLoading(true)
          getFournisseurDocuments(fournisseurId)
               .then(r => setDocuments(r.data ?? []))
               .catch(() => toast.error("Impossible de charger les documents du fournisseur."))
               .finally(() => setLoading(false))
     }, [fournisseurId])

     const requiredTypes = FOURNISSEUR_DOC_CONFIG.map(c => c.type)
     const provided = requiredTypes.filter(t => documents.some(d => d.type === t)).length

     async function uploadNew(type: string, file: File) {
          setBusyType(type)
          try {
               const r = await addFournisseurDocument(fournisseurId, type, file)
               setDocuments(prev => [...prev, r.data])
               toast.success("Document ajouté.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de l'envoi du document.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     async function replace(oldDoc: IFournisseurDocumentModel, type: string, file: File) {
          if (!oldDoc.id) return
          setBusyType(type)
          try {
               const r = await replaceFournisseurDocument(oldDoc.id, file)
               setDocuments(prev => prev.map(d => (d.id === oldDoc.id ? r.data : d)))
               toast.success("Document remplacé.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec du remplacement.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     function remove(doc: IFournisseurDocumentModel) {
          if (!doc.id) return
          Swal.fire({
               icon: "warning",
               title: "Supprimer ce document ?",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#d33",
               preConfirm: () =>
                    deleteFournisseurDocument(doc.id as number)
                         .then(() => setDocuments(prev => prev.filter(d => d.id !== doc.id)))
                         .catch(err => Swal.showValidationMessage(err?.response?.data?.detail ?? "Échec de la suppression.")),
          })
     }

     // Fonction appelée (pas un composant) : évite le remontage de l'input file pendant un téléversement concurrent.
     const renderUploadedRow = (doc: IFournisseurDocumentModel, cfg: Cfg) => (
          <div key={doc.id} className="d-flex align-items-center justify-content-between bg-light-success rounded p-3 mb-2">
               <div className="d-flex align-items-center text-truncate">
                    <i className="fas fa-circle-check text-success fs-4 me-3" />
                    <span className="fw-bold text-truncate">{doc.typeLabel || cfg.label}</span>
               </div>
               <div className="d-flex gap-1 flex-shrink-0">
                    <button type="button" className="btn btn-sm btn-icon btn-light-primary" title="Voir" onClick={() => setActiveDoc(doc)}>
                         <i className="fas fa-eye" />
                    </button>
                    <button
                         type="button"
                         className="btn btn-sm btn-icon btn-light-warning"
                         title="Remplacer"
                         disabled={busyType === cfg.type}
                         onClick={() => doc.id && replaceInputRefs.current[doc.id]?.click()}
                    >
                         <i className="fas fa-arrows-rotate" />
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-light-danger" title="Supprimer" disabled={busyType === cfg.type} onClick={() => remove(doc)}>
                         <i className="fas fa-trash" />
                    </button>
                    {doc.id && (
                         <input
                              type="file"
                              accept={ACCEPT}
                              className="d-none"
                              ref={el => {
                                   replaceInputRefs.current[doc.id as number] = el
                              }}
                              onChange={e => {
                                   const f = e.target.files?.[0]
                                   if (f) replace(doc, cfg.type, f)
                                   e.target.value = ""
                              }}
                         />
                    )}
               </div>
          </div>
     )

     const renderUploadZone = (cfg: Cfg) => {
          const isDragOver = dragOverType === cfg.type
          return (
               <label
                    className={`d-flex align-items-center justify-content-between rounded p-3 mb-2 cursor-pointer border border-dashed ${isDragOver ? "border-primary bg-light-primary" : "border-warning bg-light-warning"}`}
                    onDragOver={e => e.preventDefault()}
                    onDragEnter={e => {
                         e.preventDefault()
                         setDragOverType(cfg.type)
                    }}
                    onDragLeave={e => {
                         e.preventDefault()
                         setDragOverType(null)
                    }}
                    onDrop={e => {
                         e.preventDefault()
                         setDragOverType(null)
                         const f = Array.from(e.dataTransfer.files ?? [])[0]
                         if (f) uploadNew(cfg.type, f)
                    }}
               >
                    <div className="d-flex align-items-center text-muted">
                         <i className={`fas ${isDragOver ? "fa-hand-pointer text-primary" : "fa-circle-exclamation text-warning"} fs-4 me-3`} />
                         <span>{busyType === cfg.type ? "Envoi en cours…" : isDragOver ? "Déposez le fichier ici" : `Glissez-déposez ou cliquez — « ${cfg.label} »`}</span>
                    </div>
                    <span className="btn btn-sm btn-light-primary">Parcourir</span>
                    <input
                         type="file"
                         accept={ACCEPT}
                         className="d-none"
                         disabled={busyType === cfg.type}
                         onChange={e => {
                              const f = e.target.files?.[0]
                              if (f) uploadNew(cfg.type, f)
                              e.target.value = ""
                         }}
                    />
               </label>
          )
     }

     const renderSlot = (cfg: Cfg) => {
          const matching = documents.filter(d => d.type === cfg.type)
          return (
               <div className="mb-4" key={cfg.type}>
                    <div className="d-flex align-items-center mb-2">
                         <i className={`fas ${cfg.icon} text-gray-600 me-2`} />
                         <span className="fw-bold required">{cfg.label}</span>
                    </div>
                    {matching.map(doc => renderUploadedRow(doc, cfg))}
                    {matching.length === 0 && renderUploadZone(cfg)}
               </div>
          )
     }

     if (loading) return <div className="text-muted py-3">Chargement des documents du fournisseur…</div>

     return (
          <div>
               <div className="d-flex align-items-center justify-content-between mb-6 p-4 bg-light rounded">
                    <span className="fw-bold">Documents du fournisseur requis</span>
                    <span className={`badge fs-7 ${provided === requiredTypes.length ? "badge-light-success" : "badge-light-warning"}`}>
                         {provided} / {requiredTypes.length} fournis
                    </span>
               </div>

               {FOURNISSEUR_DOC_CONFIG.map(renderSlot)}

               {activeDoc && activeDoc.googleDriveId && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveId}
                         googleDriveMimetype={activeDoc.googleDriveMimeType ?? ""}
                         modalTitle={<>{activeDoc.typeLabel ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </div>
     )
}

export default FournisseurDocumentsSlots
